/**
 * Mobile product purchase bar.
 * The bar proxies Dawn's native product form so submissions continue to use
 * the theme's existing variant validation, cart notification, and cart updates.
 */
(() => {
  class StitchesStickyPurchaseBar {
    constructor(bar) {
      this.bar = bar;

      this.section = document.getElementById(
        `MainProduct-${bar.dataset.sectionId}`
      );

      if (!this.section) return;

      this.sourceButton = this.section.querySelector(
        `#ProductSubmitButton-${bar.dataset.sectionId}`
      );

      this.sourcePrice = this.section.querySelector(
        `#price-${bar.dataset.sectionId}`
      );

      this.price = bar.querySelector(
        '[data-sticky-purchase-price]'
      );

      this.button = bar.querySelector(
        '[data-sticky-purchase-submit]'
      );

      this.buttonText = bar.querySelector(
        '[data-sticky-purchase-submit-text]'
      );

      this.mobileQuery = window.matchMedia(
        '(max-width: 749px)'
      );

      if (
        !this.sourceButton ||
        !this.button ||
        !this.buttonText
      ) {
        return;
      }

      this.handleStickyButtonClick =
        this.handleStickyButtonClick.bind(this);

      this.handleViewportChange =
        this.handleViewportChange.bind(this);

      this.handleScroll =
        this.handleScroll.bind(this);

      this.button.addEventListener(
        'click',
        this.handleStickyButtonClick
      );

      this.sourceObserver = new MutationObserver(() => {
        this.sync();
      });

      this.sourceObserver.observe(this.sourceButton, {
        attributes: true,
        childList: true,
        subtree: true,
        attributeFilter: [
          'class',
          'disabled',
          'aria-disabled',
        ],
      });

      if (this.sourcePrice && this.price) {
        this.priceObserver = new MutationObserver(() => {
          this.syncPrice();
        });

        this.priceObserver.observe(this.sourcePrice, {
          attributes: true,
          childList: true,
          characterData: true,
          subtree: true,
          attributeFilter: ['class'],
        });
      }

      this.modalObserver = new MutationObserver(() => {
        this.updateVisibility();
      });

      this.modalObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ['class'],
        childList: true,
        subtree: true,
      });

      if (
        typeof this.mobileQuery.addEventListener ===
        'function'
      ) {
        this.mobileQuery.addEventListener(
          'change',
          this.handleViewportChange
        );
      } else {
        this.mobileQuery.addListener(
          this.handleViewportChange
        );
      }

      window.addEventListener(
        'scroll',
        this.handleScroll,
        { passive: true }
      );

      window.addEventListener(
        'resize',
        this.handleViewportChange
      );

      this.sync();
      this.updateVisibility();
    }

    handleStickyButtonClick() {
      const isDisabled =
        this.sourceButton.disabled ||
        this.sourceButton.getAttribute(
          'aria-disabled'
        ) === 'true';

      if (!isDisabled) {
        this.sourceButton.click();
      }
    }

    handleViewportChange() {
      this.updateVisibility();
    }

    handleScroll() {
      this.updateVisibility();
    }

    sync() {
      const isDisabled =
        this.sourceButton.disabled ||
        this.sourceButton.getAttribute(
          'aria-disabled'
        ) === 'true';

      const sourceLabel =
        this.sourceButton
          .querySelector(':scope > span')
          ?.textContent.trim() ||
        this.sourceButton.textContent.trim();

      this.button.disabled = isDisabled;

      this.button.setAttribute(
        'aria-disabled',
        String(isDisabled)
      );

      this.buttonText.textContent = sourceLabel;

      this.button.setAttribute(
        'aria-label',
        sourceLabel
      );

      this.syncPrice();
    }

    syncPrice() {
      if (!this.sourcePrice || !this.price) return;

      const salePrice =
        this.sourcePrice.querySelector(
          '.price__sale .price-item--sale'
        );

      const regularPrice =
        this.sourcePrice.querySelector(
          '.price__regular .price-item--regular'
        );

      const saleContainer =
        salePrice?.closest('.price__sale');

      const displayedSale =
        salePrice &&
        saleContainer &&
        getComputedStyle(saleContainer).display !==
          'none';

      const source = displayedSale
        ? salePrice
        : regularPrice;

      const value =
        this.sourcePrice.classList.contains('hidden')
          ? ''
          : source?.textContent.trim() || '';

      this.price.textContent = value;
      this.price.hidden = !value;
    }

    isOverlayOpen() {
      return (
        document.body.classList.contains(
          'overflow-hidden'
        ) ||
        Boolean(
          document.querySelector(
            [
              'cart-drawer.active',
              'modal-dialog[open]',
              'product-modal[open]',
              'quick-add-modal[open]',
              'details-modal[open]',
            ].join(', ')
          )
        )
      );
    }

    updateVisibility() {
      const sectionRect =
        this.section.getBoundingClientRect();

      /*
       * Show the sticky bar after the customer has scrolled
       * about 180px into the product section.
       */
      const hasScrolledIntoProduct =
        sectionRect.top < -180;

      const shouldShow =
        this.mobileQuery.matches &&
        hasScrolledIntoProduct &&
        !this.isOverlayOpen();

      this.bar.hidden = !shouldShow;

      this.bar.setAttribute(
        'aria-hidden',
        String(!shouldShow)
      );

      document.body.classList.toggle(
        'stitches-sticky-purchase-bar-visible',
        shouldShow
      );
    }
  }

  const initialize = () => {
    document
      .querySelectorAll(
        '[data-sticky-purchase-bar]'
      )
      .forEach((bar) => {
        if (
          bar.dataset
            .stickyPurchaseInitialized === 'true'
        ) {
          return;
        }

        bar.dataset.stickyPurchaseInitialized =
          'true';

        new StitchesStickyPurchaseBar(bar);
      });
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialize
    );
  } else {
    initialize();
  }

  document.addEventListener(
    'shopify:section:load',
    initialize
  );
})();